import React, { useState, useMemo } from "react";
import { venueDetailsMapping } from "../misc/venueDetailsMapping";
import "../styles/VenueCard.css";

/* helpers */
function firstWeekdayOfMonth(year, monthIndex) {
  return new Date(year, monthIndex, 1).getDay();
}
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}
function monthsInRange(startDateStr, endDateStr) {
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  const months = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) {
    months.push({ year: cur.getFullYear(), monthIndex: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}
const getMatchColor = (score) =>
  score >= 80 ? "#28a745" : score >= 60 ? "#ffc107" : "#dc3545";

// ✅ canonical key
const dateKey = (v) => {
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return String(v);
  }
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return String(v);
};

export default function VenueCard({
  venue,
  matches,
  startDate,
  endDate,
  onSelectDate,
  selectedDates = [], // array of "YYYY-MM-DD"
}) {
  const details = venueDetailsMapping[venue.name] || {};
  const imageUrl = details.imageUrl || venue.imageUrl;
  const venueName = details.title || venue.name;

  const [page, setPage] = useState(0);
  const todayIso = new Date().toISOString().slice(0, 10);

  // ✅ index matches by canonical date key
  const matchMapByDate = useMemo(
    () =>
      matches.reduce((acc, m) => {
        const key = dateKey(m.forecast_date);
        acc[key] = { ...m, forecast_date: key };
        return acc;
      }, {}),
    [matches]
  );

  const calendars = useMemo(() => {
    if (!startDate || !endDate) return [];
    return monthsInRange(startDate, endDate).map(({ year, monthIndex }) => ({
      year,
      monthIndex,
      firstDow: firstWeekdayOfMonth(year, monthIndex),
      numDays: daysInMonth(year, monthIndex),
    }));
  }, [startDate, endDate]);

  const selectedSet = useMemo(
    () => new Set(selectedDates.map(dateKey)),
    [selectedDates]
  );

  if (!calendars.length) return null;

  const { year, monthIndex, firstDow, numDays } = calendars[page];
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="venue-card upgraded">
      <img src={imageUrl} alt={venueName} className="venue-image" />
      <h3 className="venue-title">{venueName}</h3>

      <div className="calendar-panel-wrapper">
        <div className="calendar-container">
          <div className="calendar-nav">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
            >
              ‹ Prev
            </button>
            <span>
              {new Date(year, monthIndex).toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              onClick={() =>
                setPage((p) => Math.min(p + 1, calendars.length - 1))
              }
              disabled={page === calendars.length - 1}
            >
              Next ›
            </button>
          </div>

          <div className="calendar-grid header">
            {weekdays.map((d) => (
              <div key={d} className="calendar-cell header">
                {d}
              </div>
            ))}
          </div>

          <div className="calendar-grid days">
            {Array(firstDow)
              .fill(null)
              .map((_, i) => (
                <div key={`b${i}`} className="calendar-cell empty" />
              ))}

            {Array.from({ length: numDays }, (_, i) => {
              const day = i + 1;
              const iso = `${year}-${String(monthIndex + 1).padStart(
                2,
                "0"
              )}-${String(day).padStart(2, "0")}`;
              const mm = matchMapByDate[iso];
              const past = iso < todayIso;
              const outR = iso < startDate || iso > endDate;
              const hasM = !!mm && !outR;

              // ✅ compare using canonical key
              const key = hasM ? dateKey(mm.forecast_date) : null;
              const sel = hasM ? selectedSet.has(key) : false;

              return (
                <div
                  key={iso}
                  className={[
                    "calendar-cell",
                    "day",
                    hasM ? "has-match" : "",
                    sel ? "selected" : "",
                    past || outR ? "disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={
                    hasM
                      ? { backgroundColor: getMatchColor(mm.overall_score) }
                      : {}
                  }
                  // ✅ pass normalized key back up so parent always stores canonical value
                  onClick={
                    hasM
                      ? () =>
                          onSelectDate &&
                          onSelectDate({ ...mm, forecast_date: key })
                      : undefined
                  }
                  title={iso}
                >
                  {day}
                </div>
              );
            })}

            {(() => {
              const total = firstDow + numDays;
              const tail = (7 - (total % 7)) % 7;
              return Array(tail)
                .fill(null)
                .map((_, i) => (
                  <div key={`t${i}`} className="calendar-cell empty" />
                ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
